import React, { useState, useEffect, useRef, useCallback } from "react";
import logo from "../wbclogo.png"

// Memoize static dropdown data outside the component
const DROPDOWN_DATA = {
  news: [
    ["news", "/news"],
    ["events", "/events"],
  ],
};

function Nav({ isOpen }) {
  const [isSticky, setIsSticky] = useState(false);
  const timeoutRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState("");

  // Handlers
  const clearDropdownTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const handleDropdownTriggerEnter = useCallback((dropdownKey) => {
    clearDropdownTimeout();
    setActiveDropdown(dropdownKey);
    setIsDropdownOpen(true);
  }, [clearDropdownTimeout]);

  const handleDropdownTriggerLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
      setActiveDropdown("");
    }, 200);
  }, []);

  const handleDropdownContentEnter = useCallback(() => {
    clearDropdownTimeout();
    setIsDropdownOpen(true);
  }, [clearDropdownTimeout]);

  const handleDropdownContentLeave = useCallback(() => {
    handleDropdownTriggerLeave();
  }, [handleDropdownTriggerLeave]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearDropdownTimeout();
    };
  }, [clearDropdownTimeout]);

  // Sticky nav effect
  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 0);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Class helpers
  const navClass = `z-40 text-md cus fixed mx-auto container md:w-[70%] rounded-b-2xl bg-[#E9EAEC] ${
    isSticky
      ? `${isDropdownOpen ? "h-[140px]" : "h-[80px]"} bg-neutral-900 cus2 w-[80%]`
      : "w-[48%]"
  }`;

  const flexColClass = `flex flex-col ${
    isDropdownOpen ? (isSticky ? "h-[140px]" : "h-[120px]") : "h-[16px] md:h-[60px]"
  } transition-[height] duration-300`;

  const dropdownClass = `rounded-b-2xl ${
    isDropdownOpen ? "visible" : "invisible"
  } ${isSticky ? "mt-5 h-full bg-black" : "h-full -mt-[10px] bg-black/5"}`;

  return (
    <div>
      <div className={`${isSticky ? "h-[0px]" : "h-[16px] md:h-[32px]"} sticky z-40 w-full`} />
      <div className="mx-auto w-[60%] md:w-[100%] container flex justify-center rounded-xl">
        <nav className={navClass}>
          <div className="bg-[#E9EAEC] h-[32px] w-[130%] md:w-[108%] absolute mx-auto -top-8 -left-10" />
          <div className={flexColClass}>
            <div
              className={`relative md:flex hidden mx-auto ${
                isOpen ? "w-[80%]" : "w-[90%]"
              } items-center justify-evenly ${isSticky ? "text-md" : "text-lg"}`}
              style={{
                top: isSticky ? "10px" : "-20px",
                transition: "top 0.3s linear",
                color: isSticky ? "white" : "black",
              }}
            >
              <p
                className="cusa uppercase"
                name="news"
                tabIndex={0}
                role="button"
                aria-haspopup="true"
                aria-expanded={isDropdownOpen && activeDropdown === "news"}
                onMouseEnter={() => handleDropdownTriggerEnter("news")}
                onMouseLeave={handleDropdownTriggerLeave}
                onFocus={() => handleDropdownTriggerEnter("news")}
                onBlur={handleDropdownTriggerLeave}
              >
                news
              </p>
              <a href="/rankings" className="cusa">
                RANKINGS
              </a>
              <a href="/athlete" className="cusa uppercase">
                athlete
              </a>
              <a href="/states" className="cusa">
                COMMITTEES
              </a>
              <a href="/" className="cusa">
                <img
                  src={
                    isSticky
                      ? logo
                      : logo
                  }
                  className={isSticky ? "h-[55px] object-cover" : "h-[65px] object-cover"}
                  alt="logo"
                />
              </a>
              <a href="/circulars" className="cusa">
                Circulars
              </a>
              <a href="/about" className="cusa">
                ABOUT
              </a>
              <a href="/contact" className="cusa">
                CONTACT
              </a>
              <a href="/login" className="cusa uppercase">
                Login
              </a>
            </div>
            {/* Dropdown */}
            <div
              className={dropdownClass}
              onMouseEnter={handleDropdownContentEnter}
              onMouseLeave={handleDropdownContentLeave}
              tabIndex={-1}
              role="menu"
            >
              <div className="h-full flex items-center gap-8 mx-auto w-[85%] text-md px-6">
                {activeDropdown &&
                  DROPDOWN_DATA[activeDropdown]?.map((e, i) => (
                    <p
                      key={i}
                      className={`$ {
                        isSticky ? "text-white" : "text-black"
                      } opacity-70 cursor-pointer hover:opacity-100 transition-all duration-200 uppercase`}
                    >
                      <a href={e[1]}>{e[0]}</a>
                    </p>
                  ))}
              </div>
            </div>
          </div>

          {/* Mobile logo */}
          <a
            href="/"
            className={`md:hidden flex justify-center h-[45px] items-center gap-3 text-2xl ${
              isSticky ? "text-white" : "text-black"
            }`}
          >
            <img
              src={
                isSticky
                  ? logo
                  : logo
              }
              className={
                isSticky
                  ? "object-cover h-[65px]"
                  : "object-cover h-[45px] absolute top-0"
              }
              alt="logo"
            />
          </a>
        </nav>
      </div>
    </div>
  );
}

export default Nav;